import React from 'react';
import { store } from 'Models/Store';
import { Button } from 'Modules/Ui';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { TextInput } from 'Views/Components/New/TextInput';

export type BulkPricingAction = { kind: 'INHERIT' } | { kind: 'FIXED' } | { kind: 'PERCENTAGE'; amount: number };

const MODAL_OPTIONS = {
	className: 'confirm-modal',
	shouldCloseOnEsc: true,
	shouldCloseOnOverlayClick: true,
	newModal: true,
};

function StandardConfirmContent({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
	return (
		<>
			<NewModal.Header>
				<NewModal.Title>Set all prices to Standard</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				<p className="text-sm text-gray-600 pt-2">
					This resets every price on the &quot;All routes&quot; level to Standard, removing those fixed and
					percentage overrides. Route-specific prices are left unchanged.
				</p>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton onClick={onCancel}>Cancel</NewModal.CancelButton>
				<NewModal.ConfirmButton onClick={onConfirm}>Set all to Standard</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}

function FixedConfirmContent({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
	return (
		<>
			<NewModal.Header>
				<NewModal.Title>Set all prices to Fixed</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				<p className="text-sm text-gray-600 pt-2">
					This switches every price on the &quot;All routes&quot; level to Fixed mode with an empty amount,
					ready for you to enter each price. Existing fixed and percentage amounts are cleared. Route-specific
					prices are left unchanged.
				</p>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton onClick={onCancel}>Cancel</NewModal.CancelButton>
				<NewModal.ConfirmButton onClick={onConfirm}>Set all to Fixed</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}

function PercentageModalContent({
	onCancel,
	onConfirm,
}: {
	onCancel: () => void;
	onConfirm: (amount: number) => void;
}) {
	const [text, setText] = React.useState('');
	const parsed = Number(text);
	const isValid = text.trim() !== '' && Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;

	return (
		<>
			<NewModal.Header>
				<NewModal.Title>Set all prices to a percentage discount</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				<div className="flex flex-col gap-4 pt-2">
					<p className="text-sm text-gray-600">
						Applies this percentage discount to every price on the &quot;All routes&quot; level.
						Route-specific prices are left unchanged.
					</p>
					<TextInput
						label="Discount"
						type="number"
						value={text}
						onChange={e => setText(e.target.value)}
						min={0}
						max={100}
						step={0.1}
						startAdornment="%"
						placeholder="0"
						data-test-id="bulk-pricing-amount-input"
					/>
				</div>
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton onClick={onCancel}>Cancel</NewModal.CancelButton>
				<NewModal.ConfirmButton onClick={() => onConfirm(parsed)} disabled={!isValid}>
					Apply to all
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</>
	);
}

export interface UserBulkPricingToolProps {
	/** Apply the confirmed action to every "All routes" row (the host owns the row rewrite). */
	onApply: (action: BulkPricingAction) => void;
}

/**
 * Form-state fork of the shared BulkPricingModeTool: same three confirm modals, but instead of
 * mutating a shared observable array it hands the confirmed action back to the host, which
 * rewrites the "All routes" rows immutably in form state. Bulk operations deliberately touch
 * only that layer; route-specific rows are left alone so a global "10% off" doesn't wipe
 * per-route overrides.
 */
export function UserBulkPricingTool({ onApply }: UserBulkPricingToolProps) {
	const apply = (action: BulkPricingAction) => {
		onApply(action);
		store.modal.hide();
	};

	const openStandardModal = () => {
		store.modal?.show(
			'Set all prices to Standard',
			<StandardConfirmContent onCancel={() => store.modal.hide()} onConfirm={() => apply({ kind: 'INHERIT' })} />,
			MODAL_OPTIONS,
		);
	};

	const openFixedModal = () => {
		store.modal?.show(
			'Set all prices to Fixed',
			<FixedConfirmContent onCancel={() => store.modal.hide()} onConfirm={() => apply({ kind: 'FIXED' })} />,
			MODAL_OPTIONS,
		);
	};

	const openPercentageModal = () => {
		store.modal?.show(
			'Set all prices',
			<PercentageModalContent
				onCancel={() => store.modal.hide()}
				onConfirm={amount => apply({ kind: 'PERCENTAGE', amount })}
			/>,
			MODAL_OPTIONS,
		);
	};

	return (
		<div className="flex flex-wrap items-center gap-3">
			<span className="text-sm text-gray-600">Set all &quot;All routes&quot; prices to:</span>
			<Button
				type="button"
				size="sm"
				variant="outline"
				onClick={openStandardModal}
				data-test-id="bulk-pricing-standard"
			>
				Standard
			</Button>
			<Button
				type="button"
				size="sm"
				variant="outline"
				onClick={openFixedModal}
				data-test-id="bulk-pricing-fixed"
			>
				Fixed
			</Button>
			<Button
				type="button"
				size="sm"
				variant="outline"
				onClick={openPercentageModal}
				data-test-id="bulk-pricing-percentage"
			>
				Percentage
			</Button>
		</div>
	);
}

export default UserBulkPricingTool;
